/**
 * Utilitaires pour la gestion de la nomenclature UNESCO ISCED-F 2013
 * Domaines d'études au niveau Broad field
 * Conversion codes UNESCO <-> intitulés
 */

import { unescoSubjects } from '@/config/unescoSubjects'

let subjectsData = null;

/**
 * Charge les données UNESCO depuis le module
 * @returns {Promise<Array>} Les données de la nomenclature UNESCO
 */
async function loadUNESCOData() {
  if (subjectsData) {
    return subjectsData;
  }

  try {
    subjectsData = unescoSubjects;
    console.log(`✅ UNESCO subjects loaded from module: ${subjectsData.length} subjects`);
    return subjectsData;
  } catch (error) {
    console.error('Error loading UNESCO subjects:', error);
    subjectsData = []; // Fallback
    return subjectsData;
  }
}

/**
 * Convertit un code UNESCO en intitulé lisible
 * @param {string} code - Le code UNESCO (ex: "0541")
 * @returns {Promise<string>} L'intitulé correspondant ou le code si non trouvé
 */
export async function getUNESCOLabel(code) {
  if (!code) return '';
  
  try {
    const data = await loadUNESCOData();
    const subject = data.find(item => item.code === code);
    return subject ? subject.label : code; // Fallback sur le code si non trouvé
  } catch (error) {
    console.error('Error getting UNESCO label:', error);
    return code; // Fallback sur le code en cas d'erreur
  }
}

/**
 * Convertit un intitulé en code UNESCO
 * @param {string} label - L'intitulé à rechercher
 * @returns {Promise<string>} Le code correspondant ou l'intitulé si non trouvé
 */
export async function getUNESCOCode(label) {
  if (!label) return '';
  
  try {
    const data = await loadUNESCOData();
    const subject = data.find(item => item.label.toLowerCase() === label.toLowerCase());
    return subject ? subject.code : label; // Fallback sur le label si non trouvé
  } catch (error) {
    console.error('Error getting UNESCO code:', error);
    return label; // Fallback sur le label en cas d'erreur
  }
}

/**
 * Vérifie si un code UNESCO existe
 * @param {string} code - Le code à vérifier
 * @returns {Promise<boolean>} true si le code existe
 */
export async function isValidUNESCOCode(code) {
  if (!code) return false;
  
  try {
    const data = await loadUNESCOData();
    return data.some(item => item.code === code);
  } catch (error) {
    console.error('Error validating UNESCO code:', error);
    return false;
  }
}

/**
 * Retourne toutes les données UNESCO (utile pour les composants de sélection)
 * @returns {Promise<Array>} Tableau complet des matières UNESCO
 */
export async function getAllUNESCOSubjects() {
  try {
    return await loadUNESCOData();
  } catch (error) {
    console.error('Error getting all UNESCO subjects:', error);
    return [];
  }
}

/**
 * Trouve les sous-catégories d'un code parent
 * @param {string} parentCode - Le code parent (ex: "05")
 * @returns {Promise<Array>} Les sous-catégories
 */
export async function getUNESCOChildren(parentCode) {
  if (!parentCode) return [];
  
  try {
    const data = await loadUNESCOData();
    return data.filter(item => item.parent === parentCode);
  } catch (error) {
    console.error('Error getting UNESCO children:', error);
    return [];
  }
}

/**
 * Construit le chemin hiérarchique complet d'un code (parents -> enfant)
 * @param {string} code - Le code dont on veut le chemin
 * @returns {Promise<string>} Le chemin complet (ex: "Natural sciences > Mathematics")
 */
export async function getUNESCOPath(code) {
  if (!code) return '';
  
  try {
    const data = await loadUNESCOData();
    const path = [];
    let currentCode = code;
    
    // Remonte la hiérarchie jusqu'à la racine
    while (currentCode) {
      const subject = data.find(item => item.code === currentCode);
      if (!subject) break;
      
      path.unshift(subject.label); // Ajoute au début pour avoir l'ordre correct
      currentCode = subject.parent;
    }
    
    return path.join(' > ');
  } catch (error) {
    console.error('Error getting UNESCO path:', error);
    return code; // Fallback sur le code
  }
}

/**
 * Mapping des anciennes valeurs de matières vers les codes ISCED-F 2013 Broad field
 * Permet la migration des données existantes (anciens noms de matières)
 */
const LEGACY_SUBJECT_MAPPING = {
  // Core Subjects
  'english': '02',              // Arts and humanities
  'mathematics': '05',          // Natural sciences, mathematics and statistics
  'science': '05',              // Natural sciences, mathematics and statistics

  // Humanities and Social Sciences
  'history': '02',              // Arts and humanities
  'geography': '05',            // Natural sciences, mathematics and statistics
  'religious-education': '02',  // Arts and humanities
  'civics': '03',               // Social sciences, journalism and information

  // Languages
  'french': '02',               // Arts and humanities
  'spanish': '02',              // Arts and humanities
  'german': '02',               // Arts and humanities
  'italian': '02',              // Arts and humanities
  'latin': '02',                // Arts and humanities
  'mandarin-chinese': '02',     // Arts and humanities

  // Arts
  'art-design': '02',           // Arts and humanities
  'music': '02',                // Arts and humanities
  'drama': '02',                // Arts and humanities
  'dance': '02',                // Arts and humanities

  // Technology and Practical Subjects
  'design-technology': '07',    // Engineering, manufacturing and construction
  'food-technology': '07',      // Engineering, manufacturing and construction
  'computer-science': '06',     // Information and Communication Technologies (ICTs)

  // Physical Education and Health
  'physical-education': '09',   // Health and welfare
  'health-social-care': '09',   // Health and welfare
  'pshe': '09',                 // Health and welfare

  // Optional or Specialized Subjects
  'business-studies': '04',     // Business, administration and law
  'economics': '04',            // Business, administration and law
  'psychology': '03',           // Social sciences, journalism and information
  'philosophy': '02',           // Arts and humanities
  'sociology': '03',            // Social sciences, journalism and information
  'media-studies': '03',        // Social sciences, journalism and information
  'law': '04',                  // Business, administration and law

  // Vocational Subjects
  'engineering': '07',          // Engineering, manufacturing and construction
};

// Codes ISCED-F 2013 Broad field valides
const VALID_ISCED_F_CODES = new Set(['00','01','02','03','04','05','06','07','08','09','10','99']);

/**
 * Convertit une ancienne valeur de matière vers un code ISCED-F 2013 Broad field
 * Gère : anciens noms texte (ex: "mathematics") ET anciens codes CITE 3/4 chiffres (ex: "0541")
 * @param {string} legacyValue - L'ancienne valeur
 * @returns {string} Le code ISCED-F Broad field ou la valeur originale si pas de mapping
 */
export function migrateLegacySubject(legacyValue) {
  if (!legacyValue) return '';

  // Si c'est déjà un code ISCED-F Broad field valide, le retourner tel quel
  if (VALID_ISCED_F_CODES.has(legacyValue)) {
    return legacyValue;
  }

  // Si c'est un ancien code CITE numérique (3 ou 4 chiffres), extraire les 2 premiers chiffres
  if (/^\d{3,4}$/.test(legacyValue)) {
    const broadField = legacyValue.substring(0, 2);
    if (VALID_ISCED_F_CODES.has(broadField)) {
      console.log(`📦 Migration CITE→ISCED-F: "${legacyValue}" → "${broadField}"`);
      return broadField;
    }
  }

  // Rechercher dans le mapping des anciens noms texte
  const iscedfCode = LEGACY_SUBJECT_MAPPING[legacyValue.toLowerCase()];

  if (iscedfCode) {
    console.log(`📦 Migration: "${legacyValue}" → "${iscedfCode}"`);
    return iscedfCode;
  }

  console.warn(`⚠️ No ISCED-F mapping found for legacy subject: "${legacyValue}"`);
  return legacyValue;
}

/**
 * Vérifie si une valeur nécessite une migration vers ISCED-F 2013
 * @param {string} value - La valeur à vérifier
 * @returns {boolean} true si la valeur est un ancien format à migrer
 */
export function isLegacySubject(value) {
  if (!value) return false;

  // Code ISCED-F Broad field valide → pas de migration
  if (VALID_ISCED_F_CODES.has(value)) return false;

  // Ancien code CITE numérique (3 ou 4 chiffres)
  if (/^\d{3,4}$/.test(value)) return true;

  // Ancien nom texte dans le mapping
  return LEGACY_SUBJECT_MAPPING.hasOwnProperty(value.toLowerCase());
}

/**
 * Version synchrone de getUNESCOLabel pour les filtres Tabulator
 * Utilise le cache local si les données sont déjà chargées
 * @param {string} code - Le code UNESCO
 * @returns {string|null} Le label correspondant ou null si pas en cache
 */
export function getUNESCOLabelSync(code) {
  if (!code || !subjectsData) return null;
  
  try {
    const subject = subjectsData.find(item => item.code === code);
    return subject ? subject.label : null;
  } catch (error) {
    console.error('Error getting UNESCO label sync:', error);
    return null;
  }
}

/**
 * Composable Vue pour utiliser les utilitaires UNESCO
 * @returns {Object} Fonctions réactives pour Vue
 */
export function useUNESCOSubjects() {
  return {
    getLabel: getUNESCOLabel,
    getCode: getUNESCOCode,
    isValid: isValidUNESCOCode,
    getAll: getAllUNESCOSubjects,
    getChildren: getUNESCOChildren,
    getPath: getUNESCOPath,
    migrateLegacy: migrateLegacySubject,
    isLegacy: isLegacySubject,
    getLabelSync: getUNESCOLabelSync
  };
}